import React, { useState, useEffect } from 'react';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Stack from 'react-bootstrap/Stack';
import Table from 'react-bootstrap/Table';
import { Download, Eye } from 'react-bootstrap-icons';
import EntityTypeSelector from './EntityTypeSelector';
import FilterBuilder from './FilterBuilder';
import { useReportFieldsQuery } from '../../queries/report.query';
import {
  useGenerateReportMutation,
  usePreviewReportMutation
} from '../../mutations/report.mutation';
import {
  ReportEntityType,
  ReportFilter,
  ReportBuilderRequest,
  entityTypeToNumber,
  filterOperatorToNumber
} from '../../types/report.types';

function ReportBuilder() {
  const [entityType, setEntityType] = useState<ReportEntityType>('Client');
  const [filters, setFilters] = useState<ReportFilter[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [showFieldSelector, setShowFieldSelector] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const { data: fields, isLoading: fieldsLoading } = useReportFieldsQuery({ entityType });
  const generateMutation = useGenerateReportMutation();
  const previewMutation = usePreviewReportMutation();

  // Reset filters and selected fields when entity type changes
  useEffect(() => {
    setFilters([]);
    setSelectedFields([]);
  }, [entityType]);

  const buildRequest = (): ReportBuilderRequest => {
    // Only include filters that have a field selected
    const validFilters = filters
      .filter((f) => f.field)
      .map((f) => ({
        field: f.field,
        operator: filterOperatorToNumber(f.operator),
        value: f.value
      }));

    return {
      entityType: entityTypeToNumber(entityType),
      filters: validFilters,
      selectedFields
    };
  };

  const handleGenerateReport = () => {
    const request = buildRequest();
    generateMutation.mutate(request);
  };

  const handlePreview = () => {
    const request = buildRequest();
    previewMutation.mutate(request, {
      onSuccess: () => setShowPreview(true)
    });
  };

  const toggleFieldSelection = (fieldName: string) => {
    if (selectedFields.includes(fieldName)) {
      setSelectedFields(selectedFields.filter((f) => f !== fieldName));
    } else {
      setSelectedFields([...selectedFields, fieldName]);
    }
  };

  const selectAllFields = () => {
    if (fields) {
      setSelectedFields(fields.map((f) => f.fieldName));
    }
  };

  const clearFieldSelection = () => {
    setSelectedFields([]);
  };

  const isGenerating = generateMutation.isLoading;
  const isPreviewing = previewMutation.isLoading;

  return (
    <>
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Report Builder</h5>
        </Card.Header>
        <Card.Body>
          <EntityTypeSelector entityType={entityType} onChange={setEntityType} />

          {fieldsLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" /> Loading fields...
            </div>
          ) : fields ? (
            <>
              <FilterBuilder filters={filters} fields={fields} onFiltersChange={setFilters} />

              <Card className="mb-3">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Select Fields</strong>
                    <span className="text-muted ms-2">
                      ({selectedFields.length > 0 ? selectedFields.length : 'All'} fields selected)
                    </span>
                  </div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowFieldSelector(true)}>
                    Configure Fields
                  </Button>
                </Card.Header>
              </Card>

              <Stack direction="horizontal" gap={2}>
                <Button
                  variant="outline-secondary"
                  onClick={handlePreview}
                  disabled={isPreviewing || isGenerating}>
                  {isPreviewing ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Eye className="me-1" />
                      Preview
                    </>
                  )}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleGenerateReport}
                  disabled={isGenerating || isPreviewing}>
                  {isGenerating ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="me-1" />
                      Generate Report
                    </>
                  )}
                </Button>
              </Stack>
            </>
          ) : null}
        </Card.Body>
      </Card>

      {/* Field Selector Modal */}
      <Modal show={showFieldSelector} onHide={() => setShowFieldSelector(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Select Fields to Export</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <Button variant="outline-primary" size="sm" onClick={selectAllFields} className="me-2">
              Select All
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={clearFieldSelection}>
              Clear All
            </Button>
          </div>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {fields?.map((field) => (
              <Form.Check
                key={field.fieldName}
                type="checkbox"
                id={`field-${field.fieldName}`}
                label={field.displayName}
                checked={selectedFields.includes(field.fieldName)}
                onChange={() => toggleFieldSelection(field.fieldName)}
                className="mb-2"
              />
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFieldSelector(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Preview Modal */}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            Report Preview
            {previewMutation.data && (
              <span className="text-muted ms-2" style={{ fontSize: '0.9rem' }}>
                (Showing {previewMutation.data.rows.length} of {previewMutation.data.totalCount}{' '}
                records)
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewMutation.data && (
            <div style={{ overflowX: 'auto' }}>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    {previewMutation.data.headers.map((header, index) => (
                      <th key={index}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewMutation.data.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleGenerateReport} disabled={isGenerating}>
            {isGenerating ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Generating...
              </>
            ) : (
              <>
                <Download className="me-1" />
                Download Full Report
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ReportBuilder;
